import React, { useEffect, useRef, useState } from 'react';
import MessageItem from './MessageItem';

const THINKING_PHRASES = [
  "Brillabee is thinking...",
  "Processing your question...",
  "Analyzing the problem...",
  "Crafting a response...",
  "Almost there...",
];

const ShimmeringText = ({ text }) => (
  <span
    className="bg-[length:200%_100%] animate-text-shimmer bg-clip-text text-transparent"
    style={{
      backgroundImage: 'linear-gradient(90deg, #a5b4fc 0%, #6366f1 40%, #a5b4fc 60%, #6366f1 100%)',
    }}
  >
    {text}
  </span>
);

const MessageList = ({ messages, loading, sessionId, onFeedback, gradeLevel }) => {
  const messageListRef = useRef(null);
  const [phraseIndex, setPhraseIndex] = useState(0);

  // Auto-scroll to bottom when messages change or loading starts
  useEffect(() => {
    if (messageListRef.current) {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // Cycle through thinking phrases
  useEffect(() => {
    if (!loading) {
      setPhraseIndex(0);
      return;
    }
    const interval = setInterval(() => {
      setPhraseIndex(prev => (prev + 1) % THINKING_PHRASES.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [loading]);

  return (
    <div id="message-list" className="flex-1 overflow-y-auto p-4" ref={messageListRef}>
      {(!messages || messages.length === 0) ? (
        <p className="system-message text-center text-gray-500 my-4">Start the conversation by asking a question!</p>
      ) : (
        messages.map((message, index) => (
          <div
            key={message.id || `msg-${index}`}
            className={message.isVoiceNew ? 'animate-fade-in-up' : ''}
            onAnimationEnd={message.isVoiceNew ? () => { message.isVoiceNew = false; } : undefined}
          >
            <MessageItem
              messageId={message.id}
              sessionId={sessionId}
              sender={message.sender}
              text={message.message}
              timestamp={message.timestamp}
              images={message.images}
              feedback={message.feedback}
              onFeedback={onFeedback}
              gradeLevel={gradeLevel}
            />
          </div>
        ))
      )}

      {/* Loading indicator */}
      {loading && (
        <div className="flex flex-col mb-4">
          <span className="text-xs font-medium mb-1 text-indigo-600">Brillabee</span>
          <div className="flex items-end gap-2">
            <div className="w-7 h-7 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0 mb-1 overflow-hidden">
              <img
                src="/assets/images/brillabee logo.png"
                alt="B"
                className="w-full h-full object-cover"
                onError={(e) => { e.target.style.display = 'none'; e.target.parentNode.textContent = 'B'; }}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              {/* Dots bubble */}
              <div className="message-bubble bg-[#f0f7ff] py-2.5 px-3 rounded-xl shadow-sm inline-block">
                <div className="flex items-center space-x-1.5">
                  <div className="h-2 w-2 bg-indigo-400 rounded-full animate-pulse" style={{animationDelay: "0ms"}}></div>
                  <div className="h-2 w-2 bg-indigo-400 rounded-full animate-pulse" style={{animationDelay: "300ms"}}></div>
                  <div className="h-2 w-2 bg-indigo-400 rounded-full animate-pulse" style={{animationDelay: "600ms"}}></div>
                </div>
              </div>
              {/* Cycling shimmer text below bubble */}
              <div className="h-4 ml-1" key={phraseIndex}>
                <span className="text-xs font-medium animate-fade-in-up inline-block">
                  <ShimmeringText text={THINKING_PHRASES[phraseIndex]} />
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageList;
